import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, Search, Pencil, Trash2, X, Building2, RefreshCw, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import Swal from "sweetalert2";

// Update the port/host if your Spring Boot backend uses a different address.
const API_URL = "/api/departments";
const initialForm = { departmentName: "", description: "", remark: "", status: "ACTIVE" };
const getDepartmentName = (d) => d?.departmentName ?? d?.department_name ?? "";
const errorText = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return data?.message || fallback;
};

export default function SkillCategories() {
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => { fetchDepartments(); }, []);

  async function fetchDepartments() {
    setLoading(true);
    setErrorMessage("");
    try {
      // GET /api/departments
      const response = await axios.get(API_URL);
      setDepartments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      setErrorMessage(errorText(error, "Departments load nahi ho paaye. Backend URL/server check karein."));
    } finally { setLoading(false); }
  }

  function resetForm() { setForm(initialForm); setEditingId(null); }
  function openAddForm() { resetForm(); setShowForm(true); }
  function openEditForm(d) {
    setEditingId(d.id);
    setForm({ departmentName: getDepartmentName(d), description: d.description ?? "", remark: d.remark ?? "", status: d.status ?? "ACTIVE" });
    setShowForm(true);
  }
  function closeForm() { setShowForm(false); resetForm(); }
  function handleChange(e) { setForm((old) => ({ ...old, [e.target.name]: e.target.value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    const name = form.departmentName.trim();
    if (!name) {
      await Swal.fire("Department name required", "Please enter a department name.", "warning");
      return;
    }
    const payload = { departmentName: name, description: form.description.trim(), remark: form.remark.trim(), status: form.status };
    setSaving(true);
    try {
      if (editingId !== null) {
        // PUT /api/departments/{id}
        await axios.put(`${API_URL}/${editingId}`, payload);
        await Swal.fire("Updated", "Department updated successfully.", "success");
      } else {
        // POST /api/departments
        await axios.post(API_URL, payload);
        await Swal.fire("Created", "Department added successfully.", "success");
      }
      closeForm();
      await fetchDepartments();
    } catch (error) {
      console.error("Failed to save department:", error);
      await Swal.fire("Unable to save", errorText(error, "Save failed. DepartmentRequest DTO field names check karein."), "error");
    } finally { setSaving(false); }
  }

  async function handleDelete(d) {
    const result = await Swal.fire({
      title: "Delete department?", text: `Are you sure you want to delete "${getDepartmentName(d)}"?`, icon: "warning",
      showCancelButton: true, confirmButtonText: "Yes, delete", cancelButtonText: "Cancel", confirmButtonColor: "#dc2626", reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setDeletingId(d.id);
    try {
      // DELETE /api/departments/{id}
      await axios.delete(`${API_URL}/${d.id}`);
      setDepartments((old) => old.filter((item) => item.id !== d.id));
      await Swal.fire("Deleted", "Department deleted successfully.", "success");
    } catch (error) {
      console.error("Failed to delete department:", error);
      await Swal.fire("Delete failed", errorText(error, "Department delete nahi ho paaya."), "error");
    } finally { setDeletingId(null); }
  }

  async function handleStatusFilter(value) {
    setStatusFilter(value);
    if (value === "ALL") { await fetchDepartments(); return; }
    setLoading(true); setErrorMessage("");
    try {
      // GET /api/departments/status/{status}
      const response = await axios.get(`${API_URL}/status/${value}`);
      setDepartments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to filter departments:", error);
      setErrorMessage(errorText(error, "Status-wise departments load nahi ho paaye."));
    } finally { setLoading(false); }
  }

  const filteredDepartments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return departments.filter((d) => [getDepartmentName(d), d.description ?? "", d.remark ?? "", d.status ?? ""]
      .some((value) => String(value).toLowerCase().includes(q)));
  }, [departments, search]);
  const activeCount = departments.filter((d) => d.status === "ACTIVE").length;
  const inactiveCount = departments.filter((d) => d.status === "INACTIVE").length;

  return (
    <div className="skill-categories-theme min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <style>{String.raw`
        /* =====================================================
           CERTED THEME
           #3E0F8D primary | #26065C dark | #9564DD accent
           #E4DA72 highlight | #EEEEEE page background
        ===================================================== */

        .skill-categories-theme {
          --certed-primary: #3E0F8D;
          --certed-deep: #26065C;
          --certed-accent: #9564DD;
          --certed-highlight: #E4DA72;
          --certed-bg: #EEEEEE;
          --certed-tint: #f1eafb;
          --certed-tint-2: #f8f4fd;
          --certed-border: #ddd6ea;
          --certed-text: #1f1537;
          --certed-muted: #6b6280;
          --certed-danger: #c62d4a;
          background: var(--certed-bg) !important;
          color: var(--certed-text);
        }

        /* ---------- neutral greys -> purple-tinted greys ---------- */
        .skill-categories-theme .text-gray-900 { color: var(--certed-text) !important; }
        .skill-categories-theme .text-gray-700 { color: #2f2547 !important; }
        .skill-categories-theme .text-gray-600 { color: #4d4466 !important; }
        .skill-categories-theme .text-gray-500 { color: var(--certed-muted) !important; }
        .skill-categories-theme .text-gray-400 { color: #9a91b0 !important; }
        .skill-categories-theme .text-gray-300 { color: #c9bfe0 !important; }
        .skill-categories-theme .border-gray-100 { border-color: #efe9f8 !important; }
        .skill-categories-theme .border-gray-200 { border-color: var(--certed-border) !important; }
        .skill-categories-theme .border-gray-300 { border-color: #cfc4e4 !important; }
        .skill-categories-theme .divide-gray-100 > :not([hidden]) ~ :not([hidden]) { border-color: #efe9f8 !important; }
        .skill-categories-theme input::placeholder,
        .skill-categories-theme textarea::placeholder { color: #9a91b0; }

        /* ---------- purple utility overrides ---------- */
        .skill-categories-theme .bg-purple-100 { background-color: var(--certed-tint) !important; }
        .skill-categories-theme .text-purple-700,
        .skill-categories-theme .text-purple-600 { color: var(--certed-primary) !important; }
        .skill-categories-theme .bg-purple-700 { background-color: var(--certed-primary) !important; }
        .skill-categories-theme .hover\:bg-purple-800:hover { background-color: var(--certed-deep) !important; }
        .skill-categories-theme .focus\:border-purple-500:focus { border-color: var(--certed-accent) !important; }
        .skill-categories-theme .focus\:ring-purple-100:focus { --tw-ring-color: rgba(149, 100, 221, 0.28) !important; }
        .skill-categories-theme .border-purple-500 { border-color: var(--certed-accent) !important; }
        .skill-categories-theme .certed-highlight { background: var(--certed-highlight); color: var(--certed-deep); }

        /* ---------- header banner ---------- */
        .skill-categories-theme header {
          padding: 22px 26px;
          border-radius: 20px;
          background: linear-gradient(120deg, var(--certed-deep) 0%, var(--certed-primary) 55%, var(--certed-accent) 100%);
          box-shadow: 0 12px 30px rgba(38, 6, 92, 0.28);
        }
        .skill-categories-theme header h1 { color: #ffffff !important; }
        .skill-categories-theme header p { color: rgba(255, 255, 255, 0.82) !important; }
        .skill-categories-theme header .bg-purple-100 { background-color: var(--certed-highlight) !important; }
        .skill-categories-theme header .text-purple-700 { color: var(--certed-deep) !important; }
        .skill-categories-theme header button {
          background: transparent !important;
          color: #ffffff !important;
          border-color: rgba(255, 255, 255, 0.55) !important;
          box-shadow: none !important;
        }
        .skill-categories-theme header button:hover:not(:disabled) { background: rgba(255, 255, 255, 0.16) !important; }
        .skill-categories-theme header button.bg-purple-700 {
          background: var(--certed-highlight) !important;
          color: var(--certed-deep) !important;
          border-color: var(--certed-highlight) !important;
        }
        .skill-categories-theme header button.bg-purple-700:hover:not(:disabled) { background: #efe68a !important; }

        /* ---------- stat cards ---------- */
        .skill-categories-theme section.grid > div {
          background: #ffffff;
          border: 1px solid var(--certed-border) !important;
          border-top-width: 4px !important;
          border-radius: 16px;
          box-shadow: 0 6px 18px rgba(62, 15, 141, 0.07);
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }
        .skill-categories-theme section.grid > div:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 26px rgba(62, 15, 141, 0.13);
        }
        .skill-categories-theme section.grid > div:nth-child(1) { border-top-color: var(--certed-primary) !important; }
        .skill-categories-theme section.grid > div:nth-child(2) { border-top-color: var(--certed-accent) !important; }
        .skill-categories-theme section.grid > div:nth-child(3) { border-top-color: var(--certed-highlight) !important; }
        .skill-categories-theme p.text-green-600 { color: var(--certed-primary) !important; }
        .skill-categories-theme svg.text-green-600 { color: var(--certed-accent) !important; }
        .skill-categories-theme .text-red-600 { color: var(--certed-danger) !important; }

        /* ---------- search / filter card ---------- */
        .skill-categories-theme section.mb-5 {
          border-color: var(--certed-border) !important;
          border-radius: 16px;
          box-shadow: 0 4px 14px rgba(62, 15, 141, 0.06);
        }
        .skill-categories-theme section.mb-5 input,
        .skill-categories-theme section.mb-5 select { background-color: #ffffff; color: var(--certed-text); }

        /* ---------- table ---------- */
        .skill-categories-theme section.overflow-hidden {
          border-color: var(--certed-border) !important;
          border-top: 4px solid var(--certed-highlight) !important;
          border-radius: 18px;
          box-shadow: 0 8px 24px rgba(62, 15, 141, 0.08);
        }
        .skill-categories-theme thead { background: var(--certed-primary) !important; color: #ffffff !important; }
        .skill-categories-theme thead th { letter-spacing: 0.06em; }
        .skill-categories-theme tbody tr:nth-child(even) { background: #faf7fe; }
        .skill-categories-theme tbody tr:hover { background: var(--certed-tint) !important; }
        .skill-categories-theme .hover\:bg-purple-50\/40:hover { background-color: var(--certed-tint) !important; }

        /* status badges */
        .skill-categories-theme tbody span.rounded-full.bg-green-100 { background: var(--certed-primary) !important; color: #ffffff !important; }
        .skill-categories-theme tbody span.rounded-full.bg-red-100 { background: #fdecef !important; color: var(--certed-danger) !important; }

        /* action buttons */
        .skill-categories-theme tbody .bg-blue-50 { background: #f8f5cf !important; color: #6b6000 !important; }
        .skill-categories-theme tbody .bg-blue-50:hover { background: var(--certed-highlight) !important; color: var(--certed-deep) !important; }
        .skill-categories-theme tbody .bg-red-50 { background: #fdecef !important; color: var(--certed-danger) !important; }
        .skill-categories-theme tbody .bg-red-50:hover:not(:disabled) { background: var(--certed-danger) !important; color: #ffffff !important; }

        .skill-categories-theme .overflow-x-auto::-webkit-scrollbar { height: 7px; }
        .skill-categories-theme .overflow-x-auto::-webkit-scrollbar-track { background: #f1eafb; }
        .skill-categories-theme .overflow-x-auto::-webkit-scrollbar-thumb { background: var(--certed-accent); border-radius: 10px; }

        /* ---------- add / edit modal ---------- */
        .skill-categories-theme .fixed {
          background: rgba(38, 6, 92, 0.62) !important;
          backdrop-filter: blur(4px);
        }
        .skill-categories-theme [role="dialog"] {
          border-radius: 20px;
          box-shadow: 0 30px 70px rgba(20, 0, 60, 0.45);
        }
        .skill-categories-theme [role="dialog"] > div:first-child {
          background: linear-gradient(120deg, var(--certed-deep), var(--certed-primary) 60%, var(--certed-accent));
          border-bottom: none !important;
        }
        .skill-categories-theme [role="dialog"] > div:first-child h2 { color: #ffffff !important; }
        .skill-categories-theme [role="dialog"] > div:first-child p { color: rgba(255, 255, 255, 0.82) !important; }
        .skill-categories-theme [role="dialog"] > div:first-child button { color: #ffffff !important; background: rgba(255, 255, 255, 0.16); }
        .skill-categories-theme [role="dialog"] > div:first-child button:hover:not(:disabled) {
          background: var(--certed-highlight) !important;
          color: var(--certed-deep) !important;
        }
        .skill-categories-theme [role="dialog"] form { background: #f8f4fd; }
        .skill-categories-theme [role="dialog"] label { color: var(--certed-primary) !important; }
        .skill-categories-theme [role="dialog"] input,
        .skill-categories-theme [role="dialog"] textarea,
        .skill-categories-theme [role="dialog"] select { background-color: #ffffff; color: var(--certed-text); }
        .skill-categories-theme [role="dialog"] form button[type="button"] {
          color: var(--certed-primary) !important;
          border-color: #cfc4e4 !important;
          background: #ffffff;
        }
        .skill-categories-theme [role="dialog"] form button[type="button"]:hover:not(:disabled) {
          background: var(--certed-tint) !important;
          border-color: var(--certed-accent) !important;
        }
        .skill-categories-theme [role="dialog"] form button[type="submit"] { box-shadow: 0 6px 16px rgba(62, 15, 141, 0.28); }

        /* ---------- keyboard focus ---------- */
        .skill-categories-theme button:focus-visible,
        .skill-categories-theme input:focus-visible,
        .skill-categories-theme select:focus-visible,
        .skill-categories-theme textarea:focus-visible { outline: 2px solid var(--certed-accent); outline-offset: 2px; }

        /* ---------- SweetAlert popups (rendered outside the page root) ---------- */
        .swal2-popup {
          border-radius: 20px !important;
          --swal2-confirm-button-background-color: #3E0F8D;
          --swal2-action-button-focus-box-shadow: 0 0 0 3px rgba(149, 100, 221, 0.45);
        }
        .swal2-popup .swal2-title { color: #26065C !important; }
        .swal2-container .swal2-styled.swal2-confirm { background-color: #3E0F8D; }
        .swal2-container .swal2-styled.swal2-cancel { background-color: #6b6280; }
        .swal2-container .swal2-styled:focus { box-shadow: 0 0 0 3px rgba(149, 100, 221, 0.45) !important; }
        .swal2-icon.swal2-success { border-color: #9564DD !important; color: #9564DD !important; }
        .swal2-icon.swal2-success [class^="swal2-success-line"] { background-color: #3E0F8D !important; }
        .swal2-icon.swal2-success .swal2-success-ring { border-color: rgba(149, 100, 221, 0.3) !important; }
        .swal2-icon.swal2-warning { border-color: #E4DA72 !important; color: #8a7d00 !important; }
      `}</style>
      <div className="mx-auto max-w-7xl">
        <header className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-purple-100 p-3 text-purple-700"><Building2 size={27} /></div>
            <div><h1 className="text-2xl font-bold tracking-tight text-gray-900">Department Management</h1><p className="mt-1 text-sm text-gray-500">Create, view, update, and manage departments.</p></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={fetchDepartments} disabled={loading} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-100 disabled:opacity-60"><RefreshCw size={17} className={loading ? "animate-spin" : ""} />Refresh</button>
            <button type="button" onClick={openAddForm} className="inline-flex items-center gap-2 rounded-xl bg-purple-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-purple-800"><Plus size={18} />Add Department</button>
          </div>
        </header>

        <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"><div className="flex items-center justify-between"><p className="text-sm font-medium text-gray-500">Total Departments</p><Building2 size={19} className="text-purple-600" /></div><p className="mt-3 text-3xl font-bold text-gray-900">{departments.length}</p></div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"><div className="flex items-center justify-between"><p className="text-sm font-medium text-gray-500">Active Departments</p><CheckCircle2 size={19} className="text-green-600" /></div><p className="mt-3 text-3xl font-bold text-green-600">{activeCount}</p></div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"><div className="flex items-center justify-between"><p className="text-sm font-medium text-gray-500">Inactive Departments</p><XCircle size={19} className="text-red-500" /></div><p className="mt-3 text-3xl font-bold text-red-600">{inactiveCount}</p></div>
        </section>

        <section className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"><div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1"><Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, description, remark, or status..." className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100" /></div>
          <select value={statusFilter} onChange={(e) => handleStatusFilter(e.target.value)} className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100" aria-label="Filter departments by status"><option value="ALL">All statuses</option><option value="ACTIVE">Active only</option><option value="INACTIVE">Inactive only</option></select>
        </div></section>

        {errorMessage && <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"><AlertCircle size={19} className="mt-0.5 shrink-0" /><div className="flex-1"><p className="font-semibold">Could not load departments</p><p className="mt-1">{errorMessage}</p></div><button type="button" onClick={fetchDepartments} className="shrink-0 font-semibold underline">Retry</button></div>}

        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4"><div><h2 className="font-semibold text-gray-900">Departments</h2><p className="mt-1 text-xs text-gray-500">Showing {filteredDepartments.length} of {departments.length} departments</p></div></div>
          <div className="overflow-x-auto"><table className="w-full min-w-[850px] text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500"><tr><th className="px-5 py-4 font-semibold">ID</th><th className="px-5 py-4 font-semibold">Department Name</th><th className="px-5 py-4 font-semibold">Description</th><th className="px-5 py-4 font-semibold">Remark</th><th className="px-5 py-4 font-semibold">Status</th><th className="px-5 py-4 text-center font-semibold">Actions</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={6} className="px-5 py-12 text-center text-gray-500"><span className="inline-flex items-center gap-2"><RefreshCw size={17} className="animate-spin" />Loading departments...</span></td></tr>
                : filteredDepartments.length === 0 ? <tr><td colSpan={6} className="px-5 py-12 text-center"><Building2 size={30} className="mx-auto mb-2 text-gray-300" /><p className="font-medium text-gray-700">No departments found</p><p className="mt-1 text-xs text-gray-500">Try changing your search or add a new department.</p></td></tr>
                : filteredDepartments.map((d) => <tr key={d.id} className="transition hover:bg-purple-50/40">
                  <td className="px-5 py-4 font-medium text-gray-500">{d.id}</td><td className="px-5 py-4 font-semibold text-gray-900">{getDepartmentName(d) || "—"}</td><td className="max-w-xs px-5 py-4 text-gray-600"><span className="line-clamp-2">{d.description || "—"}</span></td><td className="max-w-xs px-5 py-4 text-gray-600"><span className="line-clamp-2">{d.remark || "—"}</span></td>
                  <td className="px-5 py-4"><span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${d.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{d.status || "UNKNOWN"}</span></td>
                  <td className="px-5 py-4"><div className="flex justify-center gap-2"><button type="button" onClick={() => openEditForm(d)} title="Edit department" aria-label={`Edit ${getDepartmentName(d)}`} className="rounded-lg bg-blue-50 p-2.5 text-blue-700 hover:bg-blue-100"><Pencil size={16} /></button><button type="button" onClick={() => handleDelete(d)} disabled={deletingId === d.id} title="Delete department" aria-label={`Delete ${getDepartmentName(d)}`} className="rounded-lg bg-red-50 p-2.5 text-red-600 hover:bg-red-100 disabled:opacity-50">{deletingId === d.id ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />}</button></div></td>
                </tr>)}
            </tbody>
          </table></div>
        </section>

        {showForm && <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-950/50 p-4 backdrop-blur-[2px]" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) closeForm(); }}>
          <div role="dialog" aria-modal="true" aria-labelledby="department-form-title" className="my-auto w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5"><div><h2 id="department-form-title" className="text-xl font-bold text-gray-900">{editingId !== null ? "Edit Department" : "Add Department"}</h2><p className="mt-1 text-sm text-gray-500">Fill in the department information below.</p></div><button type="button" onClick={closeForm} disabled={saving} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50" aria-label="Close form"><X size={20} /></button></div>
            <form onSubmit={handleSubmit} className="space-y-5 p-6">
              <div><label htmlFor="departmentName" className="mb-1.5 block text-sm font-semibold text-gray-700">Department Name <span className="text-red-500">*</span></label><input id="departmentName" name="departmentName" type="text" value={form.departmentName} onChange={handleChange} maxLength={100} required autoFocus placeholder="e.g. Human Resources" className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100" /><p className="mt-1 text-right text-xs text-gray-400">{form.departmentName.length}/100</p></div>
              <div><label htmlFor="description" className="mb-1.5 block text-sm font-semibold text-gray-700">Description</label><textarea id="description" name="description" value={form.description} onChange={handleChange} maxLength={255} rows={3} placeholder="Describe this department" className="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100" /></div>
              <div><label htmlFor="remark" className="mb-1.5 block text-sm font-semibold text-gray-700">Remark</label><textarea id="remark" name="remark" value={form.remark} onChange={handleChange} maxLength={255} rows={2} placeholder="Optional remark" className="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100" /></div>
              <div><label htmlFor="status" className="mb-1.5 block text-sm font-semibold text-gray-700">Status</label><select id="status" name="status" value={form.status} onChange={handleChange} className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"><option value="ACTIVE">ACTIVE</option><option value="INACTIVE">INACTIVE</option></select></div>
              <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end"><button type="button" onClick={closeForm} disabled={saving} className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button><button type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-xl bg-purple-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60">{saving && <RefreshCw size={16} className="animate-spin" />}{saving ? "Saving..." : editingId !== null ? "Update Department" : "Save Department"}</button></div>
            </form>
          </div>
        </div>}
      </div>
    </div>
  );
}